import { useCallback, useRef } from "react";
import { supabase } from "../lib/supabase";
import { useAuth } from "../contexts/AuthContext";
import { useRole } from "../contexts/RoleContext";
import { isPlugAndPlay } from "./usePlayMode";

const EDGE_URL = `${import.meta.env.VITE_PUBLIC_SUPABASE_URL}/functions/v1/gemini-npc`;

// ─── AI CEO 페르소나 ──────────────────────────────────────────────
const CEO_PERSONA = `당신은 가상 엔터테인먼트 회사의 AI CEO '김준혁' 대표입니다.
- 결단력 있고 전략적이지만, 팀원들의 의견을 중시합니다
- 직무에 맞는 구체적인 업무 지시와 피드백을 줍니다
- 한국 엔터 업계 관행을 잘 알고 있습니다
- 항상 한국어로, 비즈니스 이메일 형식으로 답변합니다
- 2~3단락으로 간결하게 작성합니다
- 발신자 호칭은 직무+담당자 (예: 마케팅 담당자, 재무 담당자)`;

const ROLE_LABEL: Record<string, string> = {
  AR: "A&R 담당자",
  Finance: "재무 담당자",
  Marketing: "마케팅 담당자",
  Legal: "법무 담당자",
  PD: "공연기획 PD",
  Projects: "사업팀장",
  Editor: "에디터",
  Contract: "계약 MD",
  Creative: "크리에이티브 디렉터",
  AE: "영업 AE",
  Production: "영상 PD",
  Data: "데이터 분석가",
};

// AI 텍스트 생성
async function generateCEOResponse(prompt: string, context: string): Promise<string> {
  try {
    const res = await fetch(EDGE_URL, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        npcType: "ceo_feedback",
        userMessage: prompt,
        history: [],
        context: `${CEO_PERSONA}\n\n${context}`,
      }),
    });
    const data = await res.json();
    return data.reply ?? "죄송합니다. 현재 답장이 지연되고 있습니다. 곧 답변드리겠습니다.";
  } catch {
    return "네, 확인했습니다. 계속 진행해주세요.";
  }
}

// collab_messages에 CEO 메일 전송
async function sendCEOMail(params: {
  workspaceId: string;
  receiverRole: string;
  taskId: string;
  taskTitle: string;
  subject: string;
  body: string;
}) {
  await supabase.from("collab_messages").insert({
    workspace_id: params.workspaceId,
    sender_user_id: "00000000-0000-0000-0000-000000000001", // AI CEO 시스템 ID
    sender_role: "CEO",
    receiver_role: params.receiverRole,
    task_id: params.taskId,
    task_title: params.taskTitle,
    subject: params.subject,
    body: params.body,
    is_read: false,
  });
}

export function useAiCEO() {
  const { user } = useAuth();
  const { jobRole, companyConfig } = useRole();
  const sentOnboarding = useRef(false);

  const workspaceId = localStorage.getItem("kap_solo_workspace_id");

  // ── 1. 입사 환영 메일 ────────────────────────────────────────
  const sendWelcomeMail = useCallback(async () => {
    if (!isPlugAndPlay() || !workspaceId || !user || sentOnboarding.current) return;
    const key = `kap_ceo_welcomed_${workspaceId}_${jobRole}`;
    if (localStorage.getItem(key)) return;
    sentOnboarding.current = true;
    localStorage.setItem(key, "1");

    const roleLabel = ROLE_LABEL[jobRole] ?? jobRole;
    const prompt = `신입 ${roleLabel}에게 보내는 입사 첫날 환영 + 오늘 우선 처리해야 할 업무 지시 메일을 작성해주세요. 회사: ${companyConfig.label}, 직무: ${roleLabel}`;
    const body = await generateCEOResponse(prompt, `회사 상황: 루나&스타즈 EP 발매 준비 중, DarkNova 활동 재개 예정, 자본금 5,000만원으로 운영 중`);

    await sendCEOMail({
      workspaceId,
      receiverRole: jobRole,
      taskId: "onboarding",
      taskTitle: "신규 입사 온보딩",
      subject: `[CEO] ${roleLabel} 입사를 환영합니다 — 오늘 우선 업무 안내`,
      body,
    });
  }, [workspaceId, user, jobRole, companyConfig]);

  // ── 2. 결재 제출 시 CEO 피드백 ───────────────────────────────
  const sendApprovalFeedback = useCallback(async (params: {
    docTitle: string;
    docType: string;
    docContent: string;
    amount?: number;
  }) => {
    if (!isPlugAndPlay() || !workspaceId) return;

    const roleLabel = ROLE_LABEL[jobRole] ?? jobRole;
    const prompt = `${roleLabel}이 제출한 결재 서류에 대해 CEO 김준혁이 검토 의견과 승인/반려 여부를 포함한 답장을 작성해주세요.
결재 제목: ${params.docTitle}
결재 유형: ${params.docType}
주요 내용: ${params.docContent}
${params.amount ? `금액: ${params.amount.toLocaleString()}만원` : ""}`;

    const body = await generateCEOResponse(prompt, "직원이 전자결재를 제출했습니다. CEO로서 검토 후 피드백을 주세요.");

    await sendCEOMail({
      workspaceId,
      receiverRole: jobRole,
      taskId: `approval_${Date.now()}`,
      taskTitle: params.docTitle,
      subject: `[CEO 검토] RE: ${params.docTitle} — 대표이사 검토 의견`,
      body,
    });
  }, [workspaceId, jobRole]);

  // ── 3. 이벤트 대응 결과에 대한 CEO 평가 ──────────────────────
  const sendEventFeedback = useCallback(async (params: {
    eventTitle: string;
    eventDescription: string;
    chosenAction: string;
    result: { reputation: number; capital: number };
  }) => {
    if (!isPlugAndPlay() || !workspaceId) return;

    const roleLabel = ROLE_LABEL[jobRole] ?? jobRole;
    const resultText = [
      params.result.reputation !== 0 ? `명성 ${params.result.reputation > 0 ? "+" : ""}${params.result.reputation}pt` : "",
      params.result.capital !== 0 ? `자본금 ${params.result.capital > 0 ? "+" : ""}${params.result.capital.toLocaleString()}만원` : "",
    ].filter(Boolean).join(", ");

    const prompt = `회사에 이벤트가 발생해 ${roleLabel}이 대응을 선택했습니다. CEO로서 해당 판단에 대한 평가와 후속 지시를 담은 메일을 작성해주세요.

이벤트: ${params.eventTitle}
상황: ${params.eventDescription}
${roleLabel}의 선택: ${params.chosenAction}
결과: ${resultText || "영향 없음"}`;

    const body = await generateCEOResponse(prompt, "직원의 이벤트 대응을 평가하고 후속 지시를 내려주세요.");

    const isPositive = params.result.reputation >= 0 && params.result.capital >= 0;

    await sendCEOMail({
      workspaceId,
      receiverRole: jobRole,
      taskId: `event_${Date.now()}`,
      taskTitle: params.eventTitle,
      subject: `[CEO] ${isPositive ? "✓ 잘 대응했습니다" : "⚠ 대응 결과 검토 필요"} — ${params.eventTitle}`,
      body,
    });
  }, [workspaceId, jobRole]);

  // ── 4. 협업 메일을 CEO에게 보낼 경우 AI CEO 자동 답장 ──────
  const replyToCEOMail = useCallback(async (params: {
    originalSubject: string;
    originalBody: string;
    taskTitle: string;
    originalMsgId: string;
  }) => {
    if (!isPlugAndPlay() || !workspaceId) return;

    const roleLabel = ROLE_LABEL[jobRole] ?? jobRole;
    const prompt = `${roleLabel}으로부터 협업 요청 메일을 받았습니다. CEO 김준혁으로서 적절한 답장을 작성해주세요.

메일 제목: ${params.originalSubject}
메일 내용: ${params.originalBody}`;

    const body = await generateCEOResponse(prompt, "팀원이 CEO에게 협업 요청 메일을 보냈습니다.");

    await sendCEOMail({
      workspaceId,
      receiverRole: jobRole,
      taskId: `ceo_reply_${Date.now()}`,
      taskTitle: params.taskTitle,
      subject: `RE: ${params.originalSubject}`,
      body,
    });
  }, [workspaceId, jobRole]);

  // ── 5. 주간 업무 피드백 (랜덤 체크인) ──────────────────────
  const sendWeeklyCheckIn = useCallback(async () => {
    if (!isPlugAndPlay() || !workspaceId) return;
    const key = `kap_ceo_checkin_${workspaceId}_${jobRole}_${new Date().toDateString()}`;
    if (localStorage.getItem(key)) return;
    localStorage.setItem(key, "1");

    const roleLabel = ROLE_LABEL[jobRole] ?? jobRole;
    const prompt = `${roleLabel}에게 CEO가 보내는 주간 업무 점검 및 독려 메일을 작성해주세요. 현재 진행 중인 루나&스타즈 EP 발매, DarkNova 컴백 등 주요 프로젝트 언급 포함.`;

    const body = await generateCEOResponse(prompt, "주간 업무 점검 메일을 작성해주세요.");

    await sendCEOMail({
      workspaceId,
      receiverRole: jobRole,
      taskId: "weekly_checkin",
      taskTitle: "주간 업무 체크인",
      subject: `[CEO 주간 체크인] ${roleLabel}, 이번 주 업무 현황 공유 요청`,
      body,
    });
  }, [workspaceId, jobRole]);

  return {
    sendWelcomeMail,
    sendApprovalFeedback,
    sendEventFeedback,
    replyToCEOMail,
    sendWeeklyCheckIn,
    isPlugAndPlay: isPlugAndPlay(),
  };
}
